import { action, makeObservable, observable } from "mobx";
import type { ILLMExecutorStepOutputActionBase } from "./LLMExecutorStepOutputAction";
import {
    LLMExecutorStepOutputAction,
    LLMExecutorStepOutputActionType,
} from "./LLMExecutorStepOutputAction";
import { ConditionPrimitive } from "./Conditions/ConditionPrimitive";
import type { JsonSchemaRoot } from "components/JsonSchema/types";
import { LLMConstants } from "../constants";

export interface ILLMExecutorStepPromptConfiguration {
    prompt: string;
    expectedOutputs: ILLMExecutorStepPromptConfigurationOutputVariable[];
    actions: ILLMExecutorStepOutputActionBase[];
    useStructuredOutputs: boolean;
    structuredOutputJsonSchema?: JsonSchemaRoot;
}

export interface ILLMExecutorStepPromptConfigurationOutputVariable {
    name: string;
    type: LLMExecutorStepPromptConfigurationOutputVariableType;
    enumValues: string[];
    requried: boolean;
}

export enum LLMExecutorStepPromptConfigurationOutputVariableType {
    String = 1,
    Enum = 2,
}

export class LLMExecutorStepPromptConfiguration
    implements ILLMExecutorStepPromptConfiguration
{
    @observable prompt: string;
    @observable
    expectedOutputs: ILLMExecutorStepPromptConfigurationOutputVariable[];
    @observable actions: LLMExecutorStepOutputAction[];
    @observable useStructuredOutputs: boolean;
    @observable structuredOutputJsonSchema?: JsonSchemaRoot | undefined;

    constructor();
    constructor(configuration: ILLMExecutorStepPromptConfiguration);
    constructor(configuration?: ILLMExecutorStepPromptConfiguration) {
        makeObservable(this);

        if (configuration) {
            Object.assign(this, configuration);
            this.actions = configuration.actions.map(
                // @ts-ignore
                (action) => new LLMExecutorStepOutputAction(action),
            );
        } else {
            this.prompt = `
You will be analyzing a call transcript. Here is the call transcript:

<call_transcript>
{{transcript}}
</call_transcript>

Write your reasoning inside <reasoning> tags.

State your final decision inside <decision> tags.

Remember to base your analysis solely on the information provided in the transcript and the given conditions.
            `.trim();
            this.expectedOutputs = [];
            this.actions = [];
            this.useStructuredOutputs = false;
            this.structuredOutputJsonSchema = undefined;
        }
    }

    @action
    public setPrompt(prompt: string) {
        this.prompt = prompt;
    }

    @action
    public addAction(
        type: LLMExecutorStepOutputActionType = LLMExecutorStepOutputActionType.ExecuteStep,
    ) {
        const action = new LLMExecutorStepOutputAction(type);
        action.addCondition(new ConditionPrimitive());

        this.actions.push(action);
    }

    @action
    public removeAction(action: LLMExecutorStepOutputAction) {
        const index = this.actions.indexOf(action);
        if (index < 0) return false;
        this.actions.splice(index, 1);
        return true;
    }

    @action
    public setUseStructuredOutputs(useStructuredOutputs: boolean) {
        this.useStructuredOutputs = useStructuredOutputs;

        for (const action of this.actions) {
            action.setExtendedMetadataClassifierId("");
        }
    }

    @action
    public setStructuredOutputJsonSchema(schema?: JsonSchemaRoot) {
        this.structuredOutputJsonSchema = schema;
    }

    private static readonly ignoreTheseAngleBracketVariables = [
        "call_transcript",
        "transcript",
    ];

    /**
     * Distinct variables that this step could output, either structured outputs or
     * variables defined by angle brackets (< and >) in the prompt.
     * @returns
     */
    public getPotentialOutputVariables(): string[] | undefined {
        if (!this.prompt || this.prompt.length === 0) return;

        const matches = this.prompt.match(/<([^/>]*)>/g);
        if (!matches || matches.length === 0) return;

        // Extract everything between < and >
        const extractedMatches = matches.map((match) =>
            match.substring(1, match.length - 1).trim(),
        );

        const filteredMatches = extractedMatches.filter(
            (match) =>
                match.length > 0 &&
                !LLMExecutorStepPromptConfiguration.ignoreTheseAngleBracketVariables.includes(
                    match,
                ),
        );

        const variables = new Set(filteredMatches);

        if (this.useStructuredOutputs) {
            variables.add(LLMConstants.StructuredOutputVariableName);
        }

        return Array.from(variables);
    }

    /**
     *
     * Extracts distinct strings from the prompt that are enclosed in curly brackets {{ and }}.
     * @returns `undefined` if there is no prompt or there are no suitable matches. `Set<string>` otherwise
     */
    public extractTemplateVariables(): Set<string> | undefined {
        if (!this.prompt || this.prompt.length === 0) return;

        const matches = this.prompt.match(/{{(.*?)}}/g);
        if (!matches || matches.length === 0) return;

        const extractedMatches = matches.map((match) =>
            match.substring(2, match.length - 2),
        );

        const filteredMatches = extractedMatches.filter(
            (match) => match.trim().length > 0,
        );

        return new Set(filteredMatches);
    }
}

export class LLMExecutorStepPromptConfigurationOutputVariable
    implements ILLMExecutorStepPromptConfigurationOutputVariable
{
    name: string;
    type: LLMExecutorStepPromptConfigurationOutputVariableType;
    enumValues: string[];
    requried: boolean;

    constructor(type: LLMExecutorStepPromptConfigurationOutputVariableType) {
        makeObservable(this);

        this.name = "";
        this.type = type;
        this.enumValues = [];
        this.requried = false;
    }

    @action
    public setName(name: string) {
        this.name = name;
    }

    @action
    public setType(type: LLMExecutorStepPromptConfigurationOutputVariableType) {
        this.setEnumValues([]);
        this.type = type;
    }

    @action
    public setEnumValues(values: string[]) {
        this.enumValues = values;
    }

    @action
    public addEnumValue(value: string = "") {
        this.enumValues.push(value);
    }

    @action
    public removeEnumValue(index: number) {
        if (index < 0) return;
        this.enumValues.splice(index, 1);
    }
}
